import { useEffect, useState } from "react";

import {
  Dataset,
  DatasetDetailResponse,
  H3Configuration
} from "@/types/dataset";

/**
 * Resolve the H3 configuration from the available context. Kept pure so the
 * same priority logic seeds the initial state synchronously (correct on the
 * first render) AND runs inside the effect when inputs change — without it,
 * `h3Configuration` is null on the first render and downstream consumers (e.g.
 * the stats payload's grid_config) miss it until the effect fires.
 */
const resolveH3Configuration = (
  dataset: Dataset | undefined,
  selectedDataset: DatasetDetailResponse | null
): H3Configuration | null => {
  // Priority 1: use h3Configuration from dataset detail response if available
  if (dataset?.h3Configuration) {
    return dataset.h3Configuration;
  }

  // Priority 2: use selectedDataset if dataset is not available
  if (!dataset && selectedDataset?.timeseries) {
    return {
      aggregationType: "AVG",
      resolution: selectedDataset?.grid_level || 8
    };
  }

  // Default
  return null;
};

export const useH3Configuration = ({
  dataset,
  selectedDataset
}: {
  dataset?: Dataset;
  selectedDataset: DatasetDetailResponse | null;
}) => {
  const [h3Configuration, setH3Configuration] =
    useState<H3Configuration | null>(() =>
      resolveH3Configuration(dataset, selectedDataset)
    );

  useEffect(() => {
    // Reuse the same priority logic that seeds the initial state, then skip the
    // update when the resolved value is unchanged so consumers don't re-render
    // on an equal config.
    const next = resolveH3Configuration(dataset, selectedDataset);
    setH3Configuration((prev) => {
      if (
        prev?.aggregationType === next?.aggregationType &&
        prev?.resolution === next?.resolution
      ) {
        return prev;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset?.h3Configuration, selectedDataset]);

  return { h3Configuration, setH3Configuration };
};
